import { CSSProperties, ComponentPropsWithoutRef, ComponentRef, Dispatch, ReactNode, SetStateAction, forwardRef, useRef } from 'react';
import { Root, Portal, Overlay, Close, Content, DialogTitle } from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';

import dialogStyles from '@/components/ui/Dialog/Dialog.module.scss';
import closeIcon from '@/assets/icons/close.svg';
import styles from './MainDialogLayout.module.scss';

const Dialog = Root;
const DialogPortal = Portal;
const DialogClose = Close;

const DialogOverlay = forwardRef<ComponentRef<typeof Overlay>, ComponentPropsWithoutRef<typeof Overlay>>(({ className, ...props }, ref) => (
  <Overlay ref={ref} className={`${dialogStyles.overlay} ${className || ''}`} {...props} />
));
DialogOverlay.displayName = Overlay.displayName;

const DialogContent = forwardRef<ComponentRef<typeof Content>, ComponentPropsWithoutRef<typeof Content>>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <Content ref={ref} className={`${styles.content} ${className || ''}`} aria-describedby={undefined} onOpenAutoFocus={(e) => e.preventDefault()} {...props}>
      {children}
    </Content>
  </DialogPortal>
));
DialogContent.displayName = Content.displayName;

interface MainDialogLayoutProps {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>> | ((open: boolean) => void);
  title: string;
  children: ReactNode;
  titleIconSrc?: string;
  contentClassName?: string;
  preventClose?: boolean;
  /**
   * Ceiling for the dialog's width, e.g. "1310px". Without it the dialog is the window less
   * its margins, which stretches a form across a wide monitor. Optional, so every existing
   * dialog is unchanged.
   */
  maxWidth?: string;
  /**
   * Let the dialog take its content's height, up to the window, instead of always filling it.
   * Short forms otherwise sit above a large empty gap. Optional, so existing dialogs keep the
   * fixed height they were built against.
   */
  fitContent?: boolean;
}

const MainDialogLayout = ({ open, onOpenChange, title, titleIconSrc, children, contentClassName, preventClose, maxWidth, fitContent }: MainDialogLayoutProps) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  const handleOpenChange = (value: boolean) => {
    if (!value && preventClose) {
      return;
    }
    if (typeof onOpenChange === 'function') {
      onOpenChange(value);
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    if (!container) return;
    e.stopPropagation();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={contentClassName}
        style={{
          ...(maxWidth ? { '--dialog-max-width': maxWidth } : {}),
          ...(fitContent ? { '--dialog-height': 'auto', '--dialog-fit': 'calc(100% - 128px)' } : {}),
        } as CSSProperties}
      >
        {/* כפתור הסגירה מכיל אייקון בלבד עם alt ריק, ולכן היה חסר שם נגיש
            לחלוטין - קורא מסך הקריא "לחצן" בלי לומר מה הוא עושה (WCAG 4.1.2).
            aria-label נדרש כאן כי אין טקסט גלוי שאפשר להישען עליו. */}
        <DialogClose className="p-4 absolute end-0" aria-label={t('close')} disabled={preventClose} hidden={preventClose}>
          <img src={closeIcon} alt="" />
        </DialogClose>
        <DialogTitle className={`heading-h3-semibold ${styles.title}`}>
          {titleIconSrc && <img src={titleIconSrc} alt="" />} {title}
        </DialogTitle>
        <div className={styles.divider}></div>
        {/* ph-mask: הטפסים הרב-שלביים שרצים כאן (נתמך חדש, פתיחת קרן, בקשת תמיכה)
            נושאים ת"ז, חשבונות בנק ופרטי משפחה - ממוסך בהקלטות PostHog (HAT-1081). */}
        <div ref={containerRef} className={`${styles.dialogContentContainer} ph-mask`} onWheel={handleWheel} tabIndex={-1}>
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MainDialogLayout;
